import { ImagePlus, X } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Button } from '@/components/ui/button'
import {
	NOTICE_IMAGE_MAX_BYTES,
	NOTICE_MAX_IMAGES,
	NOTICE_MEDIA_ACCEPT,
	NOTICE_VIDEO_MAX_BYTES,
	type NoticeMedia,
} from '@/types/notice'

const MB = 1024 * 1024
const INPUT_ID = 'notification-media'

export interface NoticeMediaSelection {
	/** Mídias já gravadas que continuam no aviso */
	existing: NoticeMedia[]
	/** Arquivos novos, enviados depois das mídias mantidas */
	files: File[]
}

interface NoticeMediaPickerProps {
	value: NoticeMediaSelection
	onChange: (value: NoticeMediaSelection) => void
	uploadProgress: number | null
}

interface MediaTile {
	key: string
	kind: 'image' | 'video'
	url: string
	remove: () => void
}

const isVideoFile = (file: File) => file.type.startsWith('video/')

/**
 * Seletor de mídias do aviso: até 10 imagens (carrossel) ou 1 vídeo, mesma regra do backend.
 * Mantém as mídias já gravadas e as novas na mesma grade, na ordem em que serão exibidas.
 */
export function NoticeMediaPicker({
	value,
	onChange,
	uploadProgress,
}: NoticeMediaPickerProps) {
	const { t } = useTranslation()
	const { existing, files } = value

	const previewUrls = useMemo(
		() => files.map((file) => URL.createObjectURL(file)),
		[files],
	)
	useEffect(() => {
		return () => {
			for (const url of previewUrls) URL.revokeObjectURL(url)
		}
	}, [previewUrls])

	const tiles: MediaTile[] = [
		...existing.map((media) => ({
			key: media.id,
			kind: media.kind,
			url: media.url,
			remove: () =>
				onChange({
					existing: existing.filter((m) => m.id !== media.id),
					files,
				}),
		})),
		...files.map((file, index) => ({
			key: `new-${index}-${file.name}-${file.lastModified}`,
			kind: isVideoFile(file) ? ('video' as const) : ('image' as const),
			url: previewUrls[index] ?? '',
			remove: () =>
				onChange({ existing, files: files.filter((_, i) => i !== index) }),
		})),
	]

	const hasVideo = tiles.some((tile) => tile.kind === 'video')
	const canAdd = !hasVideo && tiles.length < NOTICE_MAX_IMAGES

	const handleSelect = (picked: File[]) => {
		let total = tiles.length
		let currentHasVideo = hasVideo
		const accepted: File[] = []

		for (const file of picked) {
			const isVideo = isVideoFile(file)

			if (!NOTICE_MEDIA_ACCEPT.split(',').includes(file.type)) {
				toast.error(t('administration.notificationsForm.mediaTypeError'))
				continue
			}

			const maxBytes = isVideo ? NOTICE_VIDEO_MAX_BYTES : NOTICE_IMAGE_MAX_BYTES
			if (file.size > maxBytes) {
				toast.error(
					t('administration.notificationsForm.mediaSizeError', {
						max: maxBytes / MB,
					}),
				)
				continue
			}

			if (isVideo ? total > 0 : currentHasVideo) {
				toast.error(
					t('administration.notificationsForm.mediaVideoExclusiveError'),
				)
				continue
			}

			if (total >= (isVideo ? 1 : NOTICE_MAX_IMAGES)) {
				toast.error(
					t('administration.notificationsForm.mediaMaxImagesError', {
						max: NOTICE_MAX_IMAGES,
					}),
				)
				break
			}

			accepted.push(file)
			total += 1
			currentHasVideo = currentHasVideo || isVideo
		}

		if (accepted.length > 0) {
			onChange({ existing, files: [...files, ...accepted] })
		}
	}

	return (
		<div className="flex flex-col gap-2">
			{tiles.length > 0 && (
				<ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
					{tiles.map((tile, index) => (
						<li
							key={tile.key}
							className="relative aspect-video overflow-hidden rounded-md border bg-black"
						>
							{tile.kind === 'video' ? (
								// biome-ignore lint/a11y/useMediaCaption: pré-visualização no formulário
								<video
									src={tile.url}
									controls
									preload="metadata"
									className="size-full object-contain"
								/>
							) : (
								<img
									src={tile.url}
									alt=""
									className="size-full object-contain"
								/>
							)}
							<span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 font-medium text-[10px] text-white tabular-nums">
								{index + 1}
							</span>
							<Button
								type="button"
								size="icon"
								variant="ghost"
								onClick={tile.remove}
								aria-label={t('administration.notificationsForm.mediaRemove')}
								className="absolute top-1 right-1 size-7 bg-black/50 text-white hover:bg-black/70 hover:text-white"
							>
								<X className="size-4" />
							</Button>
						</li>
					))}
				</ul>
			)}

			{canAdd && (
				<label
					htmlFor={INPUT_ID}
					className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed p-5 text-center text-muted-foreground text-sm hover:bg-muted/40"
				>
					<ImagePlus className="size-6" />
					<span>
						{t(
							tiles.length === 0
								? 'administration.notificationsForm.mediaHint'
								: 'administration.notificationsForm.mediaAddMore',
						)}
					</span>
				</label>
			)}

			<input
				id={INPUT_ID}
				type="file"
				multiple
				accept={NOTICE_MEDIA_ACCEPT}
				className="sr-only"
				onChange={(event) => {
					handleSelect(Array.from(event.target.files ?? []))
					event.target.value = ''
				}}
			/>

			{tiles.length > 0 && !hasVideo && (
				<p className="text-muted-foreground text-xs">
					{t('administration.notificationsForm.mediaCount', {
						count: tiles.length,
						max: NOTICE_MAX_IMAGES,
					})}
				</p>
			)}

			{uploadProgress !== null && (
				<div className="flex items-center gap-2 text-muted-foreground text-xs">
					<div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
						<div
							className="h-full bg-kaefer-sapphire-blue transition-all"
							style={{ width: `${uploadProgress}%` }}
						/>
					</div>
					<span>{uploadProgress}%</span>
				</div>
			)}
		</div>
	)
}
